'use client';

import { getTrialEndDate } from '@magicschool/business-logic/trials';
import { Alert, Button } from '@magicschool/spellbook';
import { subscriptionPlans } from '@magicschool/supabase/user/utils';
import { format } from 'date-fns';
import { useStore } from 'features/store';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { activateTrialForUser } from '@/app/_actions/trials';
import { useAuth } from '@/hooks/useAuth';
import useIsAdminProfession from '@/hooks/useIsAdminProfession';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import UpsellPopover from './UpsellPopover';

type ActivatedTrialBannerProps = {
  onDismiss?: () => void;
};

const STORAGE_KEY = 'pendingTrialActivationBannerTrialId';

export default function ActivatedTrialBanner({ onDismiss }: ActivatedTrialBannerProps) {
  const intl = useIntl();
  const { isSignedIn, planId, userTrials } = useAuth();
  const updateUserTrials = useStore((s) => s.AuthStoreData.updateUserTrials, []);
  const { isAdminProfession } = useIsAdminProfession();

  // Check localStorage if we recently flipped a pending trial to active, backend does not track this
  const bannerNotDismissed = userTrials.some((trial) => trial.id === window.localStorage.getItem(STORAGE_KEY));
  const [bannerOpen, setBannerOpen] = useState<boolean>(bannerNotDismissed);
  const handleClose = useCallback(() => {
    setBannerOpen(false);
    window.localStorage.setItem(STORAGE_KEY, '');
    onDismiss?.();
  }, [onDismiss]);

  useEffect(() => {
    const activatePendingTrials = async () => {
      const pendingTrial = userTrials.find((trial) => trial.status === 'pending');
      if (pendingTrial) {
        const { data: activatedTrial } = await activateTrialForUser({ trialId: pendingTrial.id });

        if (activatedTrial) {
          window.localStorage.setItem(STORAGE_KEY, pendingTrial.id);
          setBannerOpen(true);
          analyticsTrack('Impression', {
            event_type: 'Impression',
            type: 'banner',
            name: 'recommend_upgrade',
            location: 'home_page',
          });
          const updatedTrials = userTrials.map((trial) =>
            trial.id === activatedTrial.id ? { ...trial, status: 'active' as const } : trial,
          );
          updateUserTrials(updatedTrials);
          onDismiss?.();
        }
      }
    };

    activatePendingTrials();
  }, [userTrials, updateUserTrials, onDismiss]);

  if (!isSignedIn || !bannerOpen || planId !== subscriptionPlans.FREE) return null;

  return (
    // Announced by the PageBannerRegion this banner renders in.
    <Alert
      role="presentation"
      onDismiss={handleClose}
      status="primary"
      variant="banner"
      text={{ dismissButtonLabel: intl.formatMessage({ id: 'close', defaultMessage: 'Close' }) }}
    >
      <Alert.Icon name="heart" />
      <Alert.Title>
        <FormattedMessage id="banner.activated-trial.title" />
      </Alert.Title>
      <Alert.Content>
        <FormattedMessage
          id="banner.activated-trial.content"
          values={{ endDate: format(getTrialEndDate(planId, userTrials), 'MMMM do') }}
        />
      </Alert.Content>
      <Alert.Actions>
        <UpsellPopover location="banner" isAdminProfession={isAdminProfession}>
          <Button size="sm" variant="ghost">
            <FormattedMessage id="banner.activated-trial.cta" />
          </Button>
        </UpsellPopover>
      </Alert.Actions>
    </Alert>
  );
}
